import { Combobox as MantineCombobox, InputBase, useCombobox } from "@mantine/core";
import { useState } from "react";

import { colors } from "#app/theme/v1/colors";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import { Text } from "#shared/view/components/Text";

type ProcessStepSelectOption = { value: string; label: string };

export type ProcessStepSelectGroup = { group: string; items: ProcessStepSelectOption[] };

type ProcessStepSelectProps = {
  "data-testid"?: string;
  "data-autofocus"?: boolean;
  data: ProcessStepSelectGroup[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  collapsedGroupLabels?: string[];
};

export const ProcessStepSelect = ({
  "data-testid": dataTestId,
  "data-autofocus": dataAutofocus,
  data,
  value,
  onChange,
  placeholder,
  disabled,
  collapsedGroupLabels = [],
}: ProcessStepSelectProps) => {
  const [search, setSearch] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  const combobox = useCombobox({
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch("");
      setExpandedGroups([]);
    },
  });

  const normalizedSearch = search.trim().toLowerCase();

  const selectedLabel =
    data.flatMap((group) => group.items).find((item) => item.value === value)?.label ?? "";

  const toggleGroup = (groupLabel: string) => {
    setExpandedGroups((current) =>
      current.includes(groupLabel)
        ? current.filter((label) => label !== groupLabel)
        : [...current, groupLabel],
    );
  };

  const handleOptionSubmit = (optionValue: string) => {
    onChange(optionValue);
    combobox.closeDropdown();
  };

  return (
    <MantineCombobox store={combobox} onOptionSubmit={handleOptionSubmit} withinPortal>
      <MantineCombobox.Target>
        <InputBase
          data-testid={dataTestId}
          data-autofocus={dataAutofocus}
          w={"100%"}
          placeholder={placeholder}
          disabled={disabled}
          rightSection={<MantineCombobox.Chevron />}
          rightSectionPointerEvents="none"
          pointer={!combobox.dropdownOpened}
          value={combobox.dropdownOpened ? search : selectedLabel}
          onChange={(event) => {
            setSearch(event.currentTarget.value);
            combobox.openDropdown();
            combobox.resetSelectedOption();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
        />
      </MantineCombobox.Target>

      <MantineCombobox.Dropdown>
        <MantineCombobox.Options>
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {data.map((group) => {
              const isCollapsible = collapsedGroupLabels.includes(group.group);
              const isExpanded = !isCollapsible || expandedGroups.includes(group.group);

              const groupToggleTestId = dataTestId ? `${dataTestId}-other-group-toggle` : undefined;

              if (!isExpanded) {
                if (group.items.length === 0) {
                  return null;
                }

                return (
                  <Flex
                    key={group.group}
                    align={"center"}
                    gap={6}
                    px={"xs"}
                    py={6}
                    onClick={() => toggleGroup(group.group)}
                    data-testid={groupToggleTestId}
                    style={{ cursor: "pointer" }}
                  >
                    <Icon name="plus" size={14} color={colors.gray[5]} />
                    <Text size="xs" c={colors.gray[6]} fw={600}>
                      {group.group}
                    </Text>
                  </Flex>
                );
              }

              const filteredItems = group.items.filter((item) =>
                item.label.toLowerCase().includes(normalizedSearch),
              );

              if (filteredItems.length === 0) {
                return null;
              }

              return (
                <MantineCombobox.Group
                  key={group.group}
                  label={
                    isCollapsible ? (
                      <Flex
                        align={"center"}
                        gap={6}
                        onClick={() => toggleGroup(group.group)}
                        data-testid={groupToggleTestId}
                        style={{ cursor: "pointer" }}
                      >
                        <Icon name="minus" size={14} color={colors.gray[5]} />
                        <span>{group.group}</span>
                      </Flex>
                    ) : (
                      group.group
                    )
                  }
                >
                  {filteredItems.map((item) => (
                    <MantineCombobox.Option key={item.value} value={item.value}>
                      {item.label}
                    </MantineCombobox.Option>
                  ))}
                </MantineCombobox.Group>
              );
            })}
          </div>
        </MantineCombobox.Options>
      </MantineCombobox.Dropdown>
    </MantineCombobox>
  );
};
